/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import _get from 'lodash/get'
import _pick from 'lodash/pick'
import _compact from 'lodash/compact'
import { ALERT_PARTS } from 'constants/alerts'
import { ERROR_CODES } from 'constants/errors'
import { ROUTES } from 'constants/routes'

export const getErrorCodes = error => {
  let result = _get(error, 'result')

  if (!result) {
    return []
  }

  if (!Array.isArray(result)) {
    result = [result]
  }

  const errorCodes = result.reduce((acc, { code, details, localizationErrorCode }) => {
    if (code === 'Validation') {
      if (Array.isArray(details)) {
        return acc.concat(details.map(item => item.code))
      }
      return acc.concat(details.code)
    }
    return acc.concat(localizationErrorCode)
  }, [])

  return _compact(errorCodes)
}

const propertyKeysByCode = {
  // 5001: ['email'],
}

const getPropertyKeysByCode = code => propertyKeysByCode[code]

const getParamsForText = (propertiesKeys, sentValues) => _pick(sentValues, propertiesKeys)

const mapAlertParamsByErrorCode = {
  [ERROR_CODES.emailIsNotConfirmed]: {
    resendConfirmEmailUrl: ROUTES.auth.resendConfirmEmail,
  },
  [ERROR_CODES.userIsInPendingState]: {
    resendConfirmEmailUrl: ROUTES.auth.resendConfirmEmail,
  },
}

const getAlertParamsByErrorCode = errorCode => mapAlertParamsByErrorCode[errorCode] || {}

export const getNormalizedValidationErrors = result => {
  let normalizedResult = result

  if (!normalizedResult) {
    return []
  }

  if (!Array.isArray(normalizedResult)) {
    normalizedResult = [result]
  }

  return normalizedResult.reduce((acc, { code, details, localizationErrorCode, message }) => {
    if (code === 'Validation') {
      if (Array.isArray(details)) {
        return acc.concat(details.map(item => ({
          code: item.code,
          message: item.message,
          key: item.key,
        })))
      }
      return acc.concat({ code: details.code, message: details.message, key: details.key })
    }
    return acc.concat({ code: localizationErrorCode, message })
  }, [])
}

export const getTextCreator = (text, alertPart, sentValues = {}) => ({ code, message }) => {
  const propertiesKeys = getPropertyKeysByCode(code)
  const localizeText = text(`alertCodes.${code}.${alertPart}`, {
    ...getParamsForText(propertiesKeys, sentValues),
    ...getAlertParamsByErrorCode(code),
  })

  if (alertPart === ALERT_PARTS.headline) {
    return localizeText
  }

  return localizeText || message
}

export const getAlertTexts = (error: any, text, sentValues?: any) => {
  const result = _get(error, 'result')

  if (!result || getErrorCodes(error).length === 0) {
    return {
      [ALERT_PARTS.headline]: text('alerts.error.headline'),
      [ALERT_PARTS.message]: text('alerts.error.message'),
    }
  }

  const getHeadline = getTextCreator(text, ALERT_PARTS.headline, sentValues)
  const getMessage = getTextCreator(text, ALERT_PARTS.message, sentValues)
  const validationErrors = getNormalizedValidationErrors(result)
  const res: any = {
    [ALERT_PARTS.message]: validationErrors.map(validationError => getMessage(validationError)).join('\n'),
  }
  const headlines = validationErrors.map(validationError => getHeadline(validationError))

  if (_compact(headlines).length > 0) {
    res[ALERT_PARTS.headline] = headlines.join(', ')
  }

  return res
}

export const getFormErrorMessages = (error, text) => {
  const result = _get(error, 'result')

  if (!result || getErrorCodes(error).length === 0) {
    return [text('alerts.error.message')]
  }

  const validationErrors = getNormalizedValidationErrors(result)

  return validationErrors.map(({ code, message }) => text(`alertCodes.${code}.message`) || message)
}
